import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Alert, Loader, Badge, Select, Dialog, Table } from '@dreamtree-org/twreact-ui';
import { GitMerge, ExternalLink, RefreshCw } from 'lucide-react';
import Can from '@rbac/Can';
import { useGitApp, errText } from './useGitApp';

const STATE_VARIANT = { open: 'success', closed: 'error', merged: 'primary' };
const STATES = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];
const METHODS = [
  { value: 'squash', label: 'Squash and merge' },
  { value: 'merge', label: 'Create a merge commit' },
  { value: 'rebase', label: 'Rebase and merge' },
];
const selVal = (v) => (v?.target ? v.target.value : (v?.value ?? v ?? ''));

/**
 * Pull requests for the app's repo.
 *
 * Merging is human-initiated only, behind a confirm — it is the one action here that
 * changes the default branch of a real repository, and it requires the `deploy`
 * capability rather than `commit`.
 */
export default function PullRequests({ appId }) {
  const { act } = useGitApp(appId);

  const [state, setState] = useState('open');
  const [pulls, setPulls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [confirm, setConfirm] = useState(null); // { pull }
  const [method, setMethod] = useState('squash');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await act('pulls', { state });
      setPulls(d.pulls || []);
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load pull requests'));
      setPulls([]);
    } finally {
      setLoading(false);
    }
  }, [act, state]);

  useEffect(() => { load(); }, [load]);

  const merge = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await act('merge', { number: confirm.pull.number, method });
      setNotice(r.merged ? `PR #${confirm.pull.number} merged (${r.sha?.slice(0, 7)}).` : `PR #${confirm.pull.number} was not merged.`);
      setConfirm(null);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Merge failed'));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'number', label: '#', render: (p) => <span className="font-mono opacity-70">#{p.number}</span> },
    {
      key: 'title',
      label: 'Title',
      render: (p) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{p.title}</span>
          {p.draft ? <Badge variant="secondary">draft</Badge> : null}
        </div>
      ),
    },
    { key: 'state', label: 'State', render: (p) => <Badge variant={STATE_VARIANT[p.state] || 'default'}>{p.state}</Badge> },
    { key: 'branches', label: 'Branches', render: (p) => <span className="font-mono text-xs">{p.base} ← {p.head}</span> },
    { key: 'user', label: 'By' },
    { key: 'updated_at', label: 'Updated' },
    {
      key: 'actions',
      label: '',
      render: (p) => (
        <div className="flex items-center gap-2 justify-end">
          {p.html_url ? (
            <a href={p.html_url} target="_blank" rel="noreferrer" className="opacity-50 hover:opacity-100" title="Open on GitHub">
              <ExternalLink size={15} />
            </a>
          ) : null}
          {p.state === 'open' ? (
            <Can module="development" action="deploy">
              <Button size="sm" variant="secondary" leftIcon={<GitMerge size={14} />} onClick={() => { setConfirm({ pull: p }); setMethod('squash'); }}>
                Merge
              </Button>
            </Can>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <div className="w-56">
          <Select label="State" value={state} options={STATES} onChange={(v) => setState(selVal(v))} />
        </div>
        <button type="button" className="opacity-50 hover:opacity-100 mt-5" onClick={load} title="Refresh">
          <RefreshCw size={15} />
        </button>
      </Card>

      {err ? <Alert variant="error" title="Pull requests">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Pull requests">{notice}</Alert> : null}

      {loading && <Loader text="Loading pull requests…" />}
      {!loading && pulls.length === 0 && <Card className="p-6 text-sm opacity-60">No {state === 'all' ? '' : state} pull requests.</Card>}
      {!loading && pulls.length > 0 && <Table data={pulls} columns={columns} pagination withAction={false} />}

      {confirm ? (
        <Dialog isOpen onClose={() => setConfirm(null)} title={`Merge PR #${confirm.pull.number}?`}>
          <div className="space-y-4">
            <p className="text-sm">
              <span className="font-medium">{confirm.pull.title}</span>
              <br />
              <span className="font-mono text-xs opacity-70">{confirm.pull.base} ← {confirm.pull.head}</span>
            </p>
            <Select label="Merge method" value={method} options={METHODS} onChange={(v) => setMethod(selVal(v))} />
            <p className="text-[11px] opacity-60">This writes to the real repository and cannot be undone from here.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button onClick={merge} loading={busy} leftIcon={<GitMerge size={14} />}>Merge</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
